import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import CustomModal from '../../../base-components/CustomModal';
import Popup from '../../../base-components/Popup';
import { encodedImagesURI } from '../../../utils/helpers';
import { getSessionUser, hasPermission, isDynamicStaffRole } from '../../../utils/roleAccess';
const BASE_URL = process.env.REACT_APP_BASE_URL;

const paymentMethods = ['Cash', 'Online', 'Jazzcash', 'Card', 'Wallet', 'Cheque', 'Bank Transfer'];

function POSCheckout({ company_id, location_id, role }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [isMemberModalOpen, setIsMemberModalOpen] = useState(false);
  const [customerType, setCustomerType] = useState('walkin'); // 'member' or 'walkin'
  const [isSuccessAlert, setIsSuccessAlert] = useState(false);
  const [isFailureAlert, setIsFailureAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [failureMessage, setFailureMessage] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [productSearch, setProductSearch] = useState('');

  const categories = ['Sports Gear', 'Tracksuits', 'Shirts', 'Trousers', 'Accessories', 'Services'];

  useEffect(() => {
    if (company_id && location_id) {
      getProducts();
      getStock();
      getMembers();
    }
  }, [company_id, location_id, selectedCategory, role]);

  const getProducts = async () => {
    try {
      let url = `${BASE_URL}products?company_id=${company_id}&location_id=${location_id}&is_active=true`;
      if (selectedCategory) {
        url += `&category=${selectedCategory}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      if (response.ok) {
        // Handle both array response and object with products property
        if (Array.isArray(data)) {
          setProducts(data);
        } else {
          setProducts(data.products || []);
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const getStock = async () => {
    try {
      const response = await fetch(`${BASE_URL}productStock?company_id=${company_id}&location_id=${location_id}`);
      const data = await response.json();
      if (response.ok) {
        // Handle both array response and object with stock property
        if (Array.isArray(data)) {
          setStock(data);
        } else {
          setStock(data.stock || []);
        }
      }
    } catch (error) {
      console.error('Error fetching stock:', error);
    }
  };

  const getMembers = async () => {
    try {
      let endpoint = '';
      if (role === 'SuperAdmin') {
        endpoint = 'members/get-all-members';
      } else if (role === 'Admin') {
        endpoint = `members/getMembersByCompanyID/${company_id}?all=true`;
      } else if (role === 'ClubAdmin') {
        endpoint = `members/getMembersByLocationID/${location_id}?all=true`;
      } else if (isDynamicStaffRole(role)) {
        endpoint = `members/getMembersByLocationID/${location_id}?all=true`;
      }
      if (endpoint) {
        const response = await fetch(BASE_URL + endpoint);
        const data = await response.json();
        if (response.ok) {
          const activeMembers = (data.members || []).filter((member) => member.is_active === true);
          setAllMembers(activeMembers);
        }
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  useEffect(() => {
    if (memberSearch.length < 2) {
      setFilteredMembers([]);
      return;
    }
    const lowerQuery = memberSearch.toLowerCase();
    const filtered = allMembers.filter((member) => {
      const combinedMemberID = `${member?.location_id?.member_id_temp || ''}${member?.member_id || ''}`.toLowerCase();
      return (
        combinedMemberID.includes(lowerQuery) ||
        member.name?.toLowerCase().includes(lowerQuery) ||
        member.phone?.toLowerCase().includes(lowerQuery) ||
        member.email?.toLowerCase().includes(lowerQuery)
      );
    });
    setFilteredMembers(filtered);
  }, [memberSearch, allMembers]);

  const getStockForProduct = (productId) => {
    const stockItem = stock.find((s) => s.product_id?._id === productId);
    return stockItem ? stockItem.quantity : 0;
  };

  const addToCart = (product) => {
    const availableStock = getStockForProduct(product._id);
    if (availableStock <= 0) {
      setIsFailureAlert(true);
      setFailureMessage('Product is out of stock');
      setIsPopupOpen(true);
      setTimeout(() => {
        setIsPopupOpen(false);
        setIsFailureAlert(false);
      }, 3000);
      return;
    }

    const existingItem = cart.find((item) => item.product_id === product._id);
    if (existingItem) {
      if (existingItem.quantity >= availableStock) {
        setIsFailureAlert(true);
        setFailureMessage('Cannot add more. Stock limit reached');
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
          setIsFailureAlert(false);
        }, 3000);
        return;
      }
      setCart(
        cart.map((item) =>
          item.product_id === product._id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setCart([
        ...cart,
        {
          product_id: product._id,
          product_name: product.name,
          product_price: product.unit_price,
          quantity: 1,
          discount: 0,
        },
      ]);
    }
  };

  const updateCartItem = (productId, field, value) => {
    setCart(
      cart.map((item) => {
        if (item.product_id === productId) {
          if (field === 'quantity') {
            const availableStock = getStockForProduct(productId);
            const newQuantity = parseInt(value) || 0;
            if (newQuantity > availableStock) {
              setIsFailureAlert(true);
              setFailureMessage('Quantity exceeds available stock');
              setIsPopupOpen(true);
              setTimeout(() => {
                setIsPopupOpen(false);
                setIsFailureAlert(false);
              }, 3000);
              return item;
            }
            return { ...item, [field]: newQuantity };
          }
          return { ...item, [field]: parseFloat(value) || 0 };
        }
        return item;
      })
    );
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter((item) => item.product_id !== productId));
  };

  const calculateSubtotal = () => {
    return cart.reduce((sum, item) => {
      const itemTotal = item.product_price * item.quantity;
      const itemDiscount = item.discount || 0;
      return sum + itemTotal - itemDiscount;
    }, 0);
  };

  const { values, handleChange, handleSubmit, setValues } = useFormik({
    initialValues: {
      discount: 0,
      discount_remarks: '',
      payment_method: 'Cash',
      transaction_id: '',
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      customer_cnic: '',
    },
    onSubmit: async (values) => {
      await handleCheckout(values);
    },
  });

  const handleCheckout = async (checkoutValues) => {
    if (cart.length === 0) {
      setIsFailureAlert(true);
      setFailureMessage('Cart is empty');
      setIsPopupOpen(true);
      setTimeout(() => {
        setIsPopupOpen(false);
        setIsFailureAlert(false);
      }, 3000);
      return;
    }

    if (checkoutValues.payment_method !== 'Cash' && !checkoutValues.transaction_id) {
      setIsFailureAlert(true);
      setFailureMessage('Transaction ID is required for non-cash payments');
      setIsPopupOpen(true);
      setTimeout(() => {
        setIsPopupOpen(false);
        setIsFailureAlert(false);
      }, 3000);
      return;
    }

    setButtonLoading(true);
    try {
      const sessionStr = sessionStorage.getItem('user-info');
      const session = JSON.parse(sessionStr);
      const sold_by = session.user._id;

      const subtotal = calculateSubtotal();
      const discount = parseFloat(checkoutValues.discount) || 0;
      const total_amount = subtotal - discount;

      const payload = {
        company_id,
        location_id,
        items: cart.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          discount: item.discount || 0,
        })),
        discount,
        discount_remarks: checkoutValues.discount_remarks || '',
        payment_method: checkoutValues.payment_method,
        transaction_id: checkoutValues.payment_method !== 'Cash' ? checkoutValues.transaction_id : '',
        member_id: customerType === 'member' && selectedMember ? selectedMember._id : null,
        // Walk-in customer details (only when member_id is not provided)
        ...(customerType === 'member' && selectedMember
          ? {}
          : {
              customer_name: checkoutValues.customer_name || null,
              customer_phone: checkoutValues.customer_phone || null,
              customer_email: checkoutValues.customer_email || null,
              customer_cnic: checkoutValues.customer_cnic || null,
            }),
        sold_by,
      };

      const response = await fetch(`${BASE_URL}pos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok) {
        setIsSuccessAlert(true);
        setSuccessMessage(`Order created successfully! Order ID: ${data.order?.order_id || 'N/A'}`);
        setIsPopupOpen(true);
        
        // Redirect to invoice after a short delay
        setTimeout(() => {
          setIsPopupOpen(false);
          setIsSuccessAlert(false);
          // Navigate to invoice view using order ID
          if (data.order?._id) {
            navigate(`/pos-invoice/${data.order._id}`);
          } else {
            // Fallback: navigate to orders page
            navigate('/pos');
          }
        }, 2000);
        
        // Reset cart and form (but don't wait for navigation)
        setCart([]);
        setSelectedMember(null);
        setCustomerType('walkin');
        setValues({
          discount: 0,
          discount_remarks: '',
          payment_method: 'Cash',
          transaction_id: '',
          customer_name: '',
          customer_phone: '',
          customer_email: '',
          customer_cnic: '',
        });
        getStock(); // Refresh stock
      } else {
        setIsFailureAlert(true);
        setFailureMessage(data.message || 'Failed to create order');
        setIsPopupOpen(true);
        setTimeout(() => {
          setIsPopupOpen(false);
          setIsFailureAlert(false);
        }, 3000);
      }
    } catch (error) {
      setIsFailureAlert(true);
      setFailureMessage('Error creating order');
      setIsPopupOpen(true);
      setTimeout(() => {
        setIsPopupOpen(false);
        setIsFailureAlert(false);
      }, 3000);
    } finally {
      setButtonLoading(false);
    }
  };

  const filteredProducts = products.filter((product) => {
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    const matchesSearch =
      !productSearch ||
      product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
      product.sku.toLowerCase().includes(productSearch.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const subtotal = calculateSubtotal();
  const discount = parseFloat(values.discount) || 0;
  const total = subtotal - discount;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {isPopupOpen && (
        <Popup
          onClose={() => setIsPopupOpen(false)}
          alertType={isSuccessAlert ? 'success' : 'failure'}
          message={isSuccessAlert ? successMessage : failureMessage}
        />
      )}

      {/* Product Selection Panel */}
      <div className="lg:col-span-2">
        <div className="bg-gx-surface rounded-gx shadow-gx p-5">
          <h2 className="font-bold text-xl text-gx-ink mb-4">Select Products</h2>

          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-gx-border bg-gx-canvas p-2 rounded-xl outline-0 text-gx-ink-soft flex-1"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Search products..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              className="border border-gx-border bg-gx-canvas p-2 rounded-xl outline-0 text-gx-ink-soft flex-1"
            />
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-h-[600px] overflow-y-auto">
            {filteredProducts.map((product) => {
              const availableStock = getStockForProduct(product._id);
              return (
                <div
                  key={product._id}
                  className={`border rounded-lg p-3 cursor-pointer transition-all ${
                    availableStock > 0
                      ? 'hover:border-gx-navy hover:shadow-md'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                  onClick={() => availableStock > 0 && addToCart(product)}
                >
                  {product.image ? (
                    <img
                      src={encodedImagesURI(product.image)}
                      alt={product.name}
                      className="w-full h-24 object-cover rounded mb-2"
                    />
                  ) : (
                    <div className="w-full h-24 bg-gx-border rounded mb-2 flex items-center justify-center text-gx-ink-soft text-xs">
                      No Image
                    </div>
                  )}
                  <p className="font-semibold text-sm text-gx-ink truncate">{product.name}</p>
                  <p className="text-xs text-gx-ink-soft">{product.category}</p>
                  <p className="text-sm font-bold text-gx-navy">Rs. {product.unit_price}</p>
                  <p className={`text-xs ${availableStock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    Stock: {availableStock}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cart and Checkout Panel */}
      <div className="lg:col-span-1">
        <div className="bg-gx-surface rounded-gx shadow-gx p-5 sticky top-5">
          <h2 className="font-bold text-xl text-gx-ink mb-4">Cart</h2>

          {/* Customer Type Selection */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gx-ink-soft mb-2">Customer Type</label>
            <div className="flex gap-4 mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="customerType"
                  value="walkin"
                  checked={customerType === 'walkin'}
                  onChange={(e) => {
                    setCustomerType(e.target.value);
                    setSelectedMember(null);
                  }}
                  className="w-4 h-4 text-gx-navy"
                />
                <span className="text-sm text-gx-ink-soft">Walk-in</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="customerType"
                  value="member"
                  checked={customerType === 'member'}
                  onChange={(e) => {
                    setCustomerType(e.target.value);
                    setSelectedMember(null);
                    // Clear walk-in customer fields when switching to member
                    setValues({
                      ...values,
                      customer_name: '',
                      customer_phone: '',
                      customer_email: '',
                      customer_cnic: '',
                    });
                  }}
                  className="w-4 h-4 text-gx-navy"
                />
                <span className="text-sm text-gx-ink-soft">Member</span>
              </label>
            </div>

            {customerType === 'member' ? (
              <>
                {selectedMember ? (
                  <div className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                    <div>
                      <p className="font-semibold text-sm">{selectedMember.name}</p>
                      <p className="text-xs text-gx-ink-soft">{selectedMember.phone}</p>
                    </div>
                    <button
                      onClick={() => setSelectedMember(null)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setIsMemberModalOpen(true)}
                    className="w-full border border-gx-border bg-gx-canvas p-2 rounded-xl text-gx-ink-soft hover:bg-gx-border"
                  >
                    Select Member
                  </button>
                )}
              </>
            ) : (
              <div className="p-3 bg-blue-50 rounded border border-blue-200">
                <p className="text-xs font-semibold text-blue-800 mb-2">Walk-In Customer Details (Optional)</p>
                <div className="space-y-2">
                  <input
                    type="text"
                    name="customer_name"
                    placeholder="Customer Name"
                    value={values.customer_name}
                    onChange={handleChange}
                    className="w-full border border-gx-border bg-gx-canvas p-2 rounded text-sm"
                  />
                  <input
                    type="text"
                    name="customer_phone"
                    placeholder="Phone Number"
                    value={values.customer_phone}
                    onChange={handleChange}
                    className="w-full border border-gx-border bg-gx-canvas p-2 rounded text-sm"
                  />
                  <input
                    type="email"
                    name="customer_email"
                    placeholder="Email (Optional)"
                    value={values.customer_email}
                    onChange={handleChange}
                    className="w-full border border-gx-border bg-gx-canvas p-2 rounded text-sm"
                  />
                  <input
                    type="text"
                    name="customer_cnic"
                    placeholder="CNIC (XXXXX-XXXXXXX-X)"
                    value={values.customer_cnic}
                    onChange={handleChange}
                    className="w-full border border-gx-border bg-gx-canvas p-2 rounded text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Cart Items */}
          <div className="max-h-[300px] overflow-y-auto mb-4">
            {cart.length === 0 ? (
              <p className="text-center text-gx-ink-soft py-4">Cart is empty</p>
            ) : (
              cart.map((item) => {
                const availableStock = getStockForProduct(item.product_id);
                const itemTotal = item.product_price * item.quantity - (item.discount || 0);
                return (
                  <div key={item.product_id} className="border-b pb-3 mb-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{item.product_name}</p>
                        <p className="text-xs text-gx-ink-soft">Rs. {item.product_price} each</p>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        className="text-red-600 hover:text-red-700 text-sm"
                      >
                        ×
                      </button>
                    </div>
                    <div className="flex gap-2 items-center mb-1">
                      <label className="text-xs text-gx-ink-soft">Qty:</label>
                      <input
                        type="number"
                        min="1"
                        max={availableStock}
                        value={item.quantity}
                        onChange={(e) => updateCartItem(item.product_id, 'quantity', e.target.value)}
                        className="w-16 border border-gx-border bg-gx-canvas p-1 rounded text-sm"
                      />
                    </div>
                    <div className="flex gap-2 items-center mb-1">
                      <label className="text-xs text-gx-ink-soft">Discount:</label>
                      <input
                        type="number"
                        min="0"
                        value={item.discount || 0}
                        onChange={(e) => updateCartItem(item.product_id, 'discount', e.target.value)}
                        className="w-20 border border-gx-border bg-gx-canvas p-1 rounded text-sm"
                      />
                    </div>
                    <p className="text-sm font-semibold text-right">Rs. {itemTotal.toFixed(2)}</p>
                  </div>
                );
              })
            )}
          </div>

          {/* Checkout Form */}
          {cart.length > 0 && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gx-ink-soft mb-2">Order Discount</label>
                <input
                  type="number"
                  name="discount"
                  min="0"
                  value={values.discount}
                  onChange={handleChange}
                  className="w-full border border-gx-border bg-gx-canvas p-2 rounded-xl outline-0"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gx-ink-soft mb-2">Discount Remarks</label>
                <input
                  type="text"
                  name="discount_remarks"
                  value={values.discount_remarks}
                  onChange={handleChange}
                  placeholder="Optional"
                  className="w-full border border-gx-border bg-gx-canvas p-2 rounded-xl outline-0"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gx-ink-soft mb-2">Payment Method</label>
                <select
                  name="payment_method"
                  value={values.payment_method}
                  onChange={handleChange}
                  className="w-full border border-gx-border bg-gx-canvas p-2 rounded-xl outline-0"
                >
                  {paymentMethods.map((method) => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </select>
              </div>

              {values.payment_method !== 'Cash' && (
                <div>
                  <label className="block text-sm font-semibold text-gx-ink-soft mb-2">
                    Transaction ID <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="transaction_id"
                    value={values.transaction_id}
                    onChange={handleChange}
                    required
                    className="w-full border border-gx-border bg-gx-canvas p-2 rounded-xl outline-0"
                  />
                </div>
              )}

              {/* Totals */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gx-ink-soft">Subtotal:</span>
                  <span className="font-semibold">Rs. {subtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Discount:</span>
                    <span>-Rs. {discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Total:</span>
                  <span className="text-gx-navy">Rs. {total.toFixed(2)}</span>
                </div>
              </div>

              {hasPermission(getSessionUser(), 'pos:checkout') && (
              <button
                type="submit"
                disabled={buttonLoading}
                className="gx-cta w-full py-3"
              >
                {buttonLoading ? 'Processing...' : 'Checkout'}
              </button>
              )}
            </form>
          )}
        </div>
      </div>

      {/* Member Search Modal */}
      <CustomModal isModal={isMemberModalOpen} handleModal={() => setIsMemberModalOpen(false)} size="lg">
        <h1 className="font-bold text-3xl text-gx-ink-soft p-5 border-b mx-2">Select Member</h1>
        <div className="m-5">
          <input
            type="text"
            placeholder="Search by name or phone..."
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            className="w-full border border-gx-border bg-gx-canvas p-3 rounded-xl outline-0 mb-4"
          />
          <div className="max-h-[400px] overflow-y-auto">
            {filteredMembers.length === 0 ? (
              <p className="text-center text-gx-ink-soft py-4">
                {memberSearch.length < 2 ? 'Type at least 2 characters to search' : 'No members found'}
              </p>
            ) : (
              filteredMembers.map((member) => (
                <div
                  key={member._id}
                  onClick={() => {
                    setSelectedMember(member);
                    setIsMemberModalOpen(false);
                    setMemberSearch('');
                    setFilteredMembers([]);
                  }}
                  className="p-3 border rounded-lg mb-2 cursor-pointer hover:bg-gx-canvas"
                >
                  <p className="font-semibold">{member.name}</p>
                  <p className="text-sm text-gx-ink-soft">{member.phone}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </CustomModal>
    </div>
  );
}

export default POSCheckout;

